import { memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeSanitize from 'rehype-sanitize';
import 'highlight.js/styles/github-dark.css';

interface MarkdownContentProps {
  content: string;
  className?: string;
}

/**
 * Splits content into main content and references section.
 * Looks for "## References" header to separate the sections.
 */
function splitReferences(content: string): { main: string; references: string | null } {
  const referencesMatch = content.match(/\n## References\s*\n/i);
  if (referencesMatch && referencesMatch.index !== undefined) {
    return {
      main: content.slice(0, referencesMatch.index).trim(),
      references: content.slice(referencesMatch.index).trim(),
    };
  }
  return { main: content, references: null };
}

/**
 * Renders markdown content with syntax highlighting and sanitization.
 *
 * Features:
 * - Full markdown support (bold, italic, lists, links, headers)
 * - Syntax highlighting for code blocks (via highlight.js)
 * - XSS protection (via rehype-sanitize)
 * - Backward compatible with plain text
 * - Special styling for References section
 */
function MarkdownContent({ content, className = '' }: MarkdownContentProps) {
  const { main, references } = splitReferences(content);

  const markdownComponents = {
    // Custom inline code styling
    code: ({ node, className, children, ...props }: any) => {
      const inline = !className;
      if (inline) {
        return (
          <code className="inline-code" {...props}>
            {children}
          </code>
        );
      }
      return (
        <code className={className} {...props}>
          {children}
        </code>
      );
    },
    // Make links open in new tab
    a: ({ node, children, href, ...props }: any) => (
      <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    ),
  };

  return (
    <div className={`stc-markdown-content ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize, rehypeHighlight]}
        components={markdownComponents}
      >
        {main}
      </ReactMarkdown>
      {references && (
        <div className="stc-markdown-content__references">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeSanitize, rehypeHighlight]}
            components={markdownComponents}
          >
            {references}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
}

// Memoize to avoid re-parsing markdown on every render
export default memo(MarkdownContent);
